import type { DashboardFilters, DashboardStats } from '@/types';

import { apiService } from '../api/index';

function unwrapDashboardStats(response: unknown): DashboardStats | undefined {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  const payload = response as Record<string, unknown>;

  if ('recentScans' in payload || 'totalPets' in payload || 'aiScansToday' in payload) {
    return payload as unknown as DashboardStats;
  }

  if (payload.data && typeof payload.data === 'object') {
    const nested = payload.data as Record<string, unknown>;
    if ('recentScans' in nested || 'totalPets' in nested || 'aiScansToday' in nested) {
      return nested as unknown as DashboardStats;
    }
  }

  return undefined;
}

export class DashboardService {
  static async getDashboardStats(filters: DashboardFilters = {}): Promise<DashboardStats> {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) {
        params.append('dateRange', JSON.stringify(filters.dateRange));
      }

      const queryString = params.toString();
      const url = `/dashboard/stats${queryString ? `?${queryString}` : ''}`;

      const response = await apiService.get<unknown>(url);
      const dashboardData = unwrapDashboardStats(response);

      if (!dashboardData) {
        return this.getEmptyDashboardStats();
      }

      return this.transformApiResponse(dashboardData);
    } catch (error: unknown) {
      const apiError = error as { response?: { status?: number } };
      console.error('DashboardService: Error fetching dashboard stats:', error);

      if (apiError.response?.status === 401) {
        return this.getEmptyDashboardStats();
      }

      return this.getEmptyDashboardStats();
    }
  }

  private static transformApiResponse(apiResponse: DashboardStats): DashboardStats {
    return {
      totalUsers: apiResponse.totalUsers ?? 0,
      totalPets: apiResponse.totalPets ?? 0,
      aiScansToday: apiResponse.aiScansToday ?? 0,
      activeSubscriptions: apiResponse.activeSubscriptions ?? 0,
      freeUsers: apiResponse.freeUsers ?? 0,
      premiumConversionRate: apiResponse.premiumConversionRate ?? 0,
      newPremiumThisWeek: apiResponse.newPremiumThisWeek ?? 0,
      gracePeriodSubscriptions: apiResponse.gracePeriodSubscriptions ?? 0,
      newUsersThisWeek: apiResponse.newUsersThisWeek ?? 0,
      newPetsThisWeek: apiResponse.newPetsThisWeek ?? 0,
      totalBreeds: apiResponse.totalBreeds ?? 0,
      totalResources: apiResponse.totalResources ?? 0,
      recentActivity: apiResponse.recentActivity ?? [],
      recentScans: apiResponse.recentScans ?? [],
    };
  }

  private static getEmptyDashboardStats(): DashboardStats {
    return {
      totalUsers: 0,
      totalPets: 0,
      aiScansToday: 0,
      activeSubscriptions: 0,
      freeUsers: 0,
      premiumConversionRate: 0,
      newPremiumThisWeek: 0,
      gracePeriodSubscriptions: 0,
      newUsersThisWeek: 0,
      newPetsThisWeek: 0,
      totalBreeds: 0,
      totalResources: 0,
      recentActivity: [],
      recentScans: [],
    };
  }

  static async getDashboardCharts(filters: DashboardFilters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) {
        params.append('dateRange', JSON.stringify(filters.dateRange));
      }

      const queryString = params.toString();
      const url = `/dashboard/charts${queryString ? `?${queryString}` : ''}`;

      const response = await apiService.get<{ data?: Record<string, unknown> } | Record<string, unknown>>(url);

      let chartsData: Record<string, unknown> | undefined;
      if (typeof response === 'object' && response !== null && 'data' in response && response.data) {
        chartsData = response.data as Record<string, unknown>;
      } else if (response && typeof response === 'object') {
        chartsData = response as Record<string, unknown>;
      }

      if (!chartsData) {
        return this.getEmptyDashboardCharts();
      }

      return this.transformChartsApiResponse(chartsData);
    } catch (error: unknown) {
      console.error('DashboardService: Error fetching dashboard charts:', error);
      return this.getEmptyDashboardCharts();
    }
  }

  private static transformChartsApiResponse(apiResponse: Record<string, unknown>) {
    return {
      scanTypeChart: apiResponse.scanTypeChart ?? [],
      scanSeverityChart: apiResponse.scanSeverityChart ?? [],
      userRoleChart: apiResponse.userRoleChart ?? [],
      subscriptionTierChart: apiResponse.subscriptionTierChart ?? [],
    };
  }

  private static getEmptyDashboardCharts() {
    return {
      scanTypeChart: [],
      scanSeverityChart: [],
      userRoleChart: [],
      subscriptionTierChart: [],
    };
  }
}

export default DashboardService;
